import React, { useState, useRef } from 'react';
import { BidGroup } from '../../types/bidding';
import { formatCurrency, recalculateGroup } from '../../utils/bidCalculator';
import TimberListEditor from './TimberListEditor';
import DesignBucketEditor from './DesignBucketEditor';
import ProductionBucketSummary from './ProductionBucketSummary';
import FreightBucketEditor from './FreightBucketEditor';
import InstallBucketEditor from './InstallBucketEditor';
import ManualMaterialsEditor from './ManualMaterialsEditor';
import './BidGroupEditor.css';

interface BidGroupEditorProps {
  groups: BidGroup[];
  onUpdate: (groups: BidGroup[]) => void;
  readOnly?: boolean;
}

const BidGroupEditor: React.FC<BidGroupEditorProps> = ({
  groups,
  onUpdate,
  readOnly = false,
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(groups.map(g => g.id))
  );
  const [expandedSections, setExpandedSections] = useState<{ [groupId: string]: Set<string> }>({});
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragNodeRef = useRef<HTMLDivElement | null>(null);

  const toggleGroup = (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const toggleSection = (groupId: string, section: string) => {
    const groupSections = expandedSections[groupId] || new Set();
    const newSections = new Set(groupSections);
    
    if (newSections.has(section)) {
      newSections.delete(section);
    } else {
      newSections.add(section);
    }
    
    setExpandedSections({ ...expandedSections, [groupId]: newSections });
  };

  const isSectionExpanded = (groupId: string, section: string): boolean => {
    return expandedSections[groupId]?.has(section) || false;
  };

  const handleUpdateGroup = (index: number, updates: Partial<BidGroup>) => {
    const updatedGroups = [...groups];
    updatedGroups[index] = recalculateGroup({ ...updatedGroups[index], ...updates });
    onUpdate(updatedGroups);
  };

  const handleAddGroup = () => {
    const newGroup: BidGroup = {
      id: `group-${Date.now()}`,
      name: `Group ${groups.length + 1}`,
      order: groups.length,
      timberList: [],
      designBucket: { lineItems: [], total: 0 },
      productionBucket: { totalBoardFeet: 0, ratePerBoardFoot: 2, total: 0 },
      freightBucket: { inbound: 0, outbound: 0, total: 0 },
      installBucket: { totalInstallDays: 1, lineItems: [], total: 0 },
      materialsBucket: { timbers: { items: [], totalCost: 0 }, manualItems: [], total: 0 },
      subtotal: 0,
      profitMargin: 0.5,
      total: 0,
    };
    onUpdate([...groups, newGroup]);
    setExpandedGroups(new Set([...Array.from(expandedGroups), newGroup.id]));
  };

  const handleDeleteGroup = (index: number) => {
    if (window.confirm(`Delete ${groups[index].name}?`)) {
      onUpdate(groups.filter((_, i) => i !== index));
    }
  };

  // Drag and Drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
    
    // Add a slight delay for the visual feedback
    setTimeout(() => {
      if (dragNodeRef.current) {
        dragNodeRef.current.style.opacity = '0.5';
      }
    }, 0);
  };

  const handleDragEnter = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear if we're leaving the container, not entering a child
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    if (
      e.clientX < rect.left ||
      e.clientX >= rect.right ||
      e.clientY < rect.top ||
      e.clientY >= rect.bottom
    ) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newGroups = [...groups];
    const [draggedGroup] = newGroups.splice(draggedIndex, 1);
    newGroups.splice(dropIndex, 0, draggedGroup);
    
    // Update order property
    newGroups.forEach((group, idx) => {
      group.order = idx;
    });

    onUpdate(newGroups);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
    if (dragNodeRef.current) {
      dragNodeRef.current.style.opacity = '1';
    }
  };

  const getGrandTotal = () => {
    return groups.reduce((sum, group) => sum + group.total, 0);
  };

  return (
    <div className="bid-group-editor">
      {groups.map((group, groupIndex) => (
        <div
          key={group.id}
          className={`bid-group-panel ${dragOverIndex === groupIndex ? 'drag-over' : ''} ${draggedIndex === groupIndex ? 'dragging' : ''}`}
          draggable={!readOnly}
          onDragStart={(e) => handleDragStart(e, groupIndex)}
          onDragEnter={(e) => handleDragEnter(e, groupIndex)}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, groupIndex)}
          onDragEnd={handleDragEnd}
          ref={draggedIndex === groupIndex ? dragNodeRef : null}
        >
          <div className="group-header" onClick={() => toggleGroup(group.id)}>
            <div className="group-header-left">
              {!readOnly && (
                <span className="drag-handle" title="Drag to reorder">⋮⋮</span>
              )}
              <span className="expand-icon">
                {expandedGroups.has(group.id) ? '▼' : '▶'}
              </span>
              {readOnly ? (
                <span className="group-name">{group.name}</span>
              ) : (
                <input
                  type="text"
                  value={group.name}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleUpdateGroup(groupIndex, { name: e.target.value });
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="group-name-input"
                />
              )}
            </div>
            <div className="group-header-right">
              <span className="group-total">{formatCurrency(group.total)}</span>
              {!readOnly && (
                <button
                  className="delete-group-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteGroup(groupIndex);
                  }}
                  title="Delete group"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {expandedGroups.has(group.id) && (
            <div className="group-content">
              {/* Timber List Section */}
              <div className="bucket-section">
                <div
                  className="section-header"
                  onClick={() => toggleSection(group.id, 'timber')}
                >
                  <span className="section-icon">
                    {isSectionExpanded(group.id, 'timber') ? '▼' : '▶'}
                  </span>
                  <span>Timber List ({group.timberList.length} items)</span>
                </div>
                {isSectionExpanded(group.id, 'timber') && (
                  <div className="section-content">
                    <TimberListEditor
                      items={group.timberList}
                      onUpdate={(timberList) => {
                        const totalBoardFeet = timberList.reduce((sum, item) => sum + (item.boardFeet * item.quantity), 0);
                        const timbersCost = timberList.reduce((sum, item) => sum + item.totalPrice, 0);
                        
                        handleUpdateGroup(groupIndex, {
                          timberList,
                          productionBucket: {
                            ...group.productionBucket,
                            totalBoardFeet,
                            total: totalBoardFeet * group.productionBucket.ratePerBoardFoot,
                          },
                          materialsBucket: {
                            ...group.materialsBucket,
                            timbers: { items: timberList, totalCost: timbersCost },
                          },
                        });
                      }}
                      readOnly={readOnly}
                    />
                  </div>
                )}
              </div>

              {/* Design Bucket */}
              <div className="bucket-section">
                <DesignBucketEditor
                  bucket={group.designBucket}
                  onUpdate={(designBucket) => handleUpdateGroup(groupIndex, { designBucket })}
                  readOnly={readOnly}
                />
              </div>

              {/* Production Bucket (Now Editable) */}
              <div className="bucket-section">
                <ProductionBucketSummary
                  bucket={group.productionBucket}
                  onUpdate={(productionBucket) => handleUpdateGroup(groupIndex, { productionBucket })}
                  readOnly={readOnly}
                />
              </div>

              {/* Freight Bucket */}
              <div className="bucket-section">
                <FreightBucketEditor
                  bucket={group.freightBucket}
                  onUpdate={(freightBucket) => handleUpdateGroup(groupIndex, { freightBucket })}
                  readOnly={readOnly}
                />
              </div>

              {/* Install Bucket */}
              <div className="bucket-section">
                <InstallBucketEditor
                  bucket={group.installBucket}
                  onUpdate={(installBucket) => handleUpdateGroup(groupIndex, { installBucket })}
                  readOnly={readOnly}
                />
              </div>

              {/* Materials Bucket - Manual Items */}
              <div className="bucket-section">
                <ManualMaterialsEditor
                  items={group.materialsBucket.manualItems}
                  onUpdate={(manualItems) => {
                    const manualTotal = manualItems.reduce((sum, item) => sum + item.totalPrice, 0);
                    handleUpdateGroup(groupIndex, {
                      materialsBucket: {
                        ...group.materialsBucket,
                        manualItems,
                        total: group.materialsBucket.timbers.totalCost + manualTotal,
                      },
                    });
                  }}
                  readOnly={readOnly}
                />
              </div>

              {/* Group Summary */}
              <div className="group-summary">
                <div className="summary-row">
                  <span>Subtotal (all buckets):</span>
                  <span>{formatCurrency(group.subtotal)}</span>
                </div>
                <div className="summary-row profit-margin-row">
                  <label>
                    Profit Margin:
                    {readOnly ? (
                      <span className="margin-display">{(group.profitMargin * 100).toFixed(1)}%</span>
                    ) : (
                      <input
                        type="number"
                        value={(group.profitMargin * 100).toFixed(1)}
                        onChange={(e) => handleUpdateGroup(groupIndex, { profitMargin: parseFloat(e.target.value) / 100 || 0 })}
                        min="0"
                        max="100"
                        step="1"
                        className="margin-input"
                      />
                    )}
                  </label>
                  <span>{formatCurrency(group.subtotal * group.profitMargin)}</span>
                </div>
                <div className="summary-row group-total-row">
                  <span><strong>Group Total:</strong></span>
                  <span><strong>{formatCurrency(group.total)}</strong></span>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}

      {!readOnly && (
        <button className="add-group-btn" onClick={handleAddGroup}>
          + Add Group
        </button>
      )}

      {groups.length > 0 && (
        <div className="grand-total-section">
          <div className="grand-total-row">
            <span className="grand-total-label">GRAND TOTAL:</span>
            <span className="grand-total-amount">{formatCurrency(getGrandTotal())}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BidGroupEditor;
